import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiMonitoringAPI } from '../../services';
import VisionBenchSampleImage from './VisionBenchSampleImage';

/**
 * Lệnh sếp 19/10/2026 — "BỘ KIỂM TRÙNG" của tab So sánh mô hình: mỗi cặp là 2 ảnh LIỀN NHAU của cùng một vùng.
 * Sếp gắn Trùng / Khác; tài khoản AI được hỏi "2 ảnh có trùng cảnh không"; bảng xếp hạng so cả thuật toán ảnh xám
 * ở nhiều ngưỡng để chọn cách lọc ảnh trùng tốt nhất.
 */
const pct = (v) => (v == null ? '—' : `${v}%`);
const num = (v) => (v == null ? '—' : v);
const time = (v) =>
  v
    ? new Date(v).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })
    : '—';
const dupText = (v) => (v == null ? '—' : v ? 'Trùng' : 'Khác');

const PairTruth = ({ pair, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (v) => {
    const next = pair.truth_dup === v ? null : v;
    setBusy(true);
    try {
      onSaved(await aiMonitoringAPI.benchSetPairTruth(pair.id, next));
    } catch (err) {
      toast.error(err?.message || 'Không lưu được đáp án');
    } finally {
      setBusy(false);
    }
  };
  return (
    <ButtonGroup
      size="sm"
      aria-label="Đáp án cặp ảnh"
    >
      <Button
        variant={pair.truth_dup === true ? 'success' : 'outline-success'}
        disabled={busy}
        onClick={() => pick(true)}
      >
        Trùng
      </Button>
      <Button
        variant={pair.truth_dup === false ? 'danger' : 'outline-danger'}
        disabled={busy}
        onClick={() => pick(false)}
      >
        Khác
      </Button>
    </ButtonGroup>
  );
};

const AiVisionBenchDup = ({ branchId, refreshKey }) => {
  const [board, setBoard] = useState(null);
  const [pairs, setPairs] = useState(null);
  const [filter, setFilter] = useState('no');
  const [page, setPage] = useState(1);

  const loadBoard = useCallback(
    () =>
      aiMonitoringAPI
        .benchPairLeaderboard(branchId)
        .then(setBoard)
        .catch(() => setBoard(null)),
    [branchId],
  );
  const loadPairs = useCallback(
    () =>
      aiMonitoringAPI
        .benchPairs({ branch_id: branchId, labeled: filter || undefined, page, limit: 12 })
        .then(setPairs)
        .catch((err) => toast.error(err?.message || 'Không tải được bộ kiểm trùng')),
    [branchId, filter, page],
  );
  useEffect(() => {
    loadBoard();
  }, [loadBoard, refreshKey]);
  useEffect(() => {
    loadPairs();
  }, [loadPairs, refreshKey]);
  useEffect(() => setPage(1), [filter, branchId]);

  const rows = board ? [...board.items, ...(board.baselines || [])] : [];
  const pages = pairs ? Math.max(1, Math.ceil(pairs.total / pairs.limit)) : 1;

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-2">Bảng xếp hạng kiểm trùng</h6>
          <p className="small text-muted mb-2">
            Mỗi cặp là 2 ảnh liền nhau của cùng vùng, chấm theo đáp án sếp gắn. <b>Gộp nhầm</b> = bảo trùng nhưng thật
            ra khác (sẽ mất ảnh). <b>Sót</b> = trùng thật mà không nhận ra (còn ảnh trùng). Các dòng nghiêng là thuật
            toán ảnh xám ở từng ngưỡng.
          </p>
          {!rows.some((r) => r.account_id || r.scored > 0) ? (
            <div className="small text-muted">
              Chưa có kết quả — gắn Trùng/Khác cho vài cặp bên dưới rồi bấm "Chạy kiểm trùng".
            </div>
          ) : (
            <Table
              size="sm"
              responsive
              hover
              className="mb-0"
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tài khoản / cách</th>
                  <th>Mô hình</th>
                  <th className="text-end">Cặp chấm</th>
                  <th className="text-end">Đúng</th>
                  <th className="text-end">Gộp nhầm</th>
                  <th className="text-end">Sót</th>
                  <th
                    className="text-end"
                    title="Trong các cặp bảo trùng, bao nhiêu % trùng thật"
                  >
                    Bảo trùng đúng
                  </th>
                  <th
                    className="text-end"
                    title="Trong các cặp trùng thật, bắt được bao nhiêu %"
                  >
                    Bắt được trùng
                  </th>
                  <th className="text-end">Lỗi</th>
                  <th className="text-end">ms/cặp</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.account_id}|${r.model}`}
                    className={r.account_id ? '' : `text-muted fst-italic${r.current ? ' fw-semibold' : ''}`}
                  >
                    <td>{r.account_id ? i + 1 : ''}</td>
                    <td>{r.account_name}</td>
                    <td className="small">{r.model}</td>
                    <td className="text-end">{r.scored}</td>
                    <td className="text-end fw-semibold">{pct(r.accuracy_pct)}</td>
                    <td className="text-end">{num(r.false_merge)}</td>
                    <td className="text-end">{num(r.missed)}</td>
                    <td className="text-end">{pct(r.precision_pct)}</td>
                    <td className="text-end">{pct(r.recall_pct)}</td>
                    <td className="text-end">{r.errors}</td>
                    <td className="text-end">{num(r.avg_latency_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h6 className="mb-0">
              Bộ kiểm trùng
              {pairs ? ` · ${pairs.totals.labeled}/${pairs.totals.all} cặp có đáp án (${pairs.totals.dup} trùng)` : ''}
            </h6>
            <Form.Select
              size="sm"
              aria-label="Lọc cặp ảnh"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="no">Chưa có đáp án</option>
              <option value="yes">Đã có đáp án</option>
              <option value="">Tất cả</option>
            </Form.Select>
          </div>
          <p className="small text-muted">
            Bấm <b>Trùng</b> nếu trong khung đỏ vẫn là cùng những người đó, cùng số người (chỉ khác cử động nhỏ); bấm{' '}
            <b>Khác</b> nếu có người đến/đi hoặc đổi người. Bấm lại nút đang chọn để bỏ đáp án. Cặp mới được ghép tự
            động mỗi giờ khi cập nhật bộ mẫu.
          </p>
          {!pairs ? (
            <div className="text-center py-3">
              <Spinner
                size="sm"
                animation="border"
              />
            </div>
          ) : pairs.items.length === 0 ? (
            <div className="small text-muted">
              Không có cặp nào — bấm "Cập nhật bộ mẫu" ở chế độ Đếm người để ghép cặp.
            </div>
          ) : (
            <Row
              xs={1}
              lg={2}
              className="g-3"
            >
              {pairs.items.map((p) => (
                <Col key={p.id}>
                  <Card
                    className="cambill-card h-100"
                    data-testid="cap-anh"
                  >
                    <Row className="g-1">
                      <Col xs={6}>
                        <div className="cambill-img-frame">
                          <VisionBenchSampleImage
                            id={p.sample_a.id}
                            box={p.sample_a.inner_box}
                            points={p.sample_a.region_points}
                          />
                        </div>
                        <div className="small text-center text-muted">A · {time(p.sample_a.detected_at)}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="cambill-img-frame">
                          <VisionBenchSampleImage
                            id={p.sample_b.id}
                            box={p.sample_b.inner_box}
                            points={p.sample_b.region_points}
                          />
                        </div>
                        <div className="small text-center text-muted">B · {time(p.sample_b.detected_at)}</div>
                      </Col>
                    </Row>
                    <Card.Body>
                      <div className="cambill-card-head">
                        <span className="cambill-card-title">{p.frame_label || '—'}</span>
                        <PairTruth
                          pair={p}
                          onSaved={(u) => {
                            setPairs((cur) => ({
                              ...cur,
                              items: cur.items.map((x) => (x.id === u.id ? { ...x, ...u } : x)),
                            }));
                            loadBoard();
                          }}
                        />
                      </div>
                      <div className="small">
                        Máy quét gốc: A <b>{p.count_a}</b> · B <b>{p.count_b}</b> · Thuật toán (≤{pairs.threshold}): độ
                        khác <b>{num(p.algo_diff)}</b> → <b>{dupText(p.algo_dup)}</b>
                      </div>
                      {p.predictions?.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {p.predictions.map((x) => (
                            <Badge
                              key={`${x.account_id}|${x.model}`}
                              bg={
                                x.predicted_dup == null
                                  ? 'secondary'
                                  : p.truth_dup == null
                                    ? 'info'
                                    : x.predicted_dup === p.truth_dup
                                      ? 'success'
                                      : 'danger'
                              }
                              title={x.error || x.model}
                            >
                              {x.account_name}: {x.predicted_dup == null ? 'lỗi' : dupText(x.predicted_dup)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          {pairs && pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((x) => x - 1)}
              >
                Trước
              </Button>
              <span className="small align-self-center">
                {page}/{pages}
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= pages}
                onClick={() => setPage((x) => x + 1)}
              >
                Sau
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default AiVisionBenchDup;
